import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useRoosterStore } from '../store/useRoosterStore.js';
import { haalTeamRooster } from '../lib/api.js';
import { weekNaarMaandag, DAG_LANG } from '../data/afwezigheid.js';
import { isoWeekNummer } from '../data/maand.js';
import { afwezigStijl } from '../lib/roostercodes.js';
import { vandaagNL, parseDag } from '../lib/tijd.js';

const DAGEN = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
const DAG_KORT = { ma: 'Ma', di: 'Di', wo: 'Wo', do: 'Do', vr: 'Vr', za: 'Za', zo: 'Zo' };
const MAANDEN = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

// Sobere, gecentreerde melding in de bestaande kaart-stijl.
function Melding({ emoji, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500">
      <div className="text-4xl mb-3">{emoji}</div>
      {children}
    </div>
  );
}

// Groepeer week.dagen PER DIENST (rij) over de zeven dagen (kolom).
// Rijen = unieke (gemeenteKey, dienstId) die deze week voorkomen; cel = namen per dag.
function bouwDienstRijen(week) {
  const rijen = new Map();
  for (const dagObj of week.dagen) {
    for (const d of dagObj.diensten) {
      const isLingewaard = d.gemeenteKey?.toLowerCase() === 'lingewaard';
      const dienstBasis = isLingewaard
        ? 'alle'
        : (d.dienstId.startsWith('late_dienst') ? d.dienstId.replace(/_(dw|we)$/, '') : d.dienstId);
      const key = `${d.gemeenteKey}|${dienstBasis}`;
      const dienstLabel = isLingewaard ? null : d.dienst;
      let rij = rijen.get(key);
      if (!rij) {
        rij = { key, gemeente: d.gemeente, dienst: dienstLabel, perDag: {}, afwezigPerDag: {} };
        rijen.set(key, rij);
      }
      rij.perDag[dagObj.dag] = [...(rij.perDag[dagObj.dag] || []), ...d.medewerkers];
      if (d.afwezigen && Object.keys(d.afwezigen).length > 0) {
        rij.afwezigPerDag[dagObj.dag] = { ...(rij.afwezigPerDag[dagObj.dag] || {}), ...d.afwezigen };
      }
    }
  }
  return [...rijen.values()].sort((a, b) =>
    `${a.gemeente} ${a.dienst || ''}`.localeCompare(`${b.gemeente} ${b.dienst || ''}`)
  );
}

// Kantoor-rij: teambreed, uit het server-veld `kantoor` per dag (rooster-service.js
// bouwKantoorPerDag). School komt hier bewust niet in — alleen kantoor.
//
// Zelfde rij-VORM als bouwDienstRijen teruggeeft, zodat zowel de tabel als de daglijst
// hem zonder verdere aanpassing renderen. Twee bewuste invullingen:
//   - `gemeente` is hier de kale kop "Kantoor" (er hoort geen gemeente vóór) en `dienst`
//     blijft null, zodat de bestaande kop-render geen ' · ' erachter zet.
//   - `afwezigPerDag` blijft leeg: de server filtert afwezigen er al uit, dus er valt
//     niets door te strepen.
// Null als deze week nergens een kantoornaam heeft → de rij blijft dan helemaal weg.
// De aanroeper plakt hem ACHTER de gesorteerde dienst-rijen, zodat hij altijd onderaan
// staat en niet mee-alfabetiseert met de gemeente-sortering hierboven.
function bouwKantoorRij(week) {
  const perDag = {};
  let aantal = 0;
  for (const dagObj of week.dagen || []) {
    const namen = dagObj.kantoor || [];
    if (namen.length === 0) continue;
    perDag[dagObj.dag] = [...namen];
    aantal += namen.length;
  }
  if (aantal === 0) return null;
  return { key: 'kantoor', gemeente: 'Kantoor', dienst: null, perDag, afwezigPerDag: {} };
}

export default function TeamRooster() {
  const isAuthenticated = useIsAuthenticated();
  const { maandSelectie, bekekenMaand, zetBekekenMaand } = useRoosterStore();

  // Lokale maand-state (peiler): geïnitialiseerd uit bekekenMaand (overleeft navigatie),
  // anders maandSelectie, anders de huidige kalendermaand. De vorige/volgende-knoppen
  // wijzigen 'm én schrijven naar de store; het fetch-effect volgt jaar/maand.
  const [peiler, setPeiler] = useState(() => {
    if (bekekenMaand) return { jaar: bekekenMaand.jaar, maand: bekekenMaand.maand };
    if (maandSelectie) return { jaar: maandSelectie.jaar, maand: maandSelectie.maand };
    const nu = new Date();
    return { jaar: nu.getFullYear(), maand: nu.getMonth() + 1 };
  });
  const { jaar, maand } = peiler;

  // Bij een maand-wissel ook de store bijwerken (zetBekekenMaand) zodat de keuze navigatie
  // overleeft. Vanuit de knop-handler, niet uit een effect (react-hooks/set-state-in-effect).
  const vorigeMaand = () => {
    const nieuw = { jaar: maand === 1 ? jaar - 1 : jaar, maand: maand === 1 ? 12 : maand - 1 };
    setPeiler(nieuw);
    zetBekekenMaand(nieuw.jaar, nieuw.maand);
  };
  const volgendeMaand = () => {
    const nieuw = { jaar: maand === 12 ? jaar + 1 : jaar, maand: maand === 12 ? 1 : maand + 1 };
    setPeiler(nieuw);
    zetBekekenMaand(nieuw.jaar, nieuw.maand);
  };

  const [data, setData] = useState(null);
  const [laden, setLaden] = useState(false);
  const [fout, setFout] = useState(null);

  // Haal het teamrooster van de server zodra login-status of maand wijzigt.
  useEffect(() => {
    if (!isAuthenticated) return;
    let geannuleerd = false;

    const laad = async () => {
      setLaden(true);
      setFout(null);
      setData(null);
      try {
        const res = await haalTeamRooster(jaar, maand);
        if (!geannuleerd) setData(res);
      } catch (err) {
        if (!geannuleerd) setFout(err.message);
      } finally {
        if (!geannuleerd) setLaden(false);
      }
    };
    laad();

    return () => { geannuleerd = true; };
  }, [isAuthenticated, jaar, maand]);

  // De ISO-week waarin VANDAAG valt — één keer per render, buiten de week-map.
  // vandaagNL() forceert Europe/Amsterdam (nooit de UTC-omweg uit lib/tijd.js: die schuift
  // 's nachts een dag), parseDag() maakt er lokale middernacht van zodat isoWeekNummer op de
  // kalenderdag rekent. Alleen weergave: dit raakt de peiler, de fetch en de store niet.
  const { week: huidigeWeek, isoJaar: huidigIsoJaar } = isoWeekNummer(parseDag(vandaagNL()));

  // Anker + eenmalige scroll naar die week. Staat er een ANDERE maand op het scherm, dan
  // matcht geen enkele week uit data.weken, krijgt geen enkele container de ref, en zet
  // React de ref bij unmount van de vorige maand zelf op null → de optional chaining hieronder
  // doet dan niets. Geen scroll, geen markering, zonder extra conditie.
  // Deps op data.weken: dat is per fetch een nieuwe array, dus dit vuurt per geladen maand
  // één keer — ook als de gebruiker wegnavigeert en terugkomt.
  const huidigeWeekRef = useRef(null);
  useEffect(() => {
    huidigeWeekRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [data?.weken]);

  const maandLabel = `${MAANDEN[maand - 1]} ${jaar}`;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Teamrooster</h1>
        <p className="text-sm text-slate-500 mt-1">
          {isAuthenticated ? 'Het volledige teamrooster per week' : 'Het teamrooster van de maand'}
        </p>
      </div>

      {/* Maand-navigatie (alleen ingelogd) */}
      {isAuthenticated && (
        <div className="flex items-center gap-2">
          <button
            onClick={vorigeMaand}
            aria-label="Vorige maand"
            className="p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-600"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm font-medium text-slate-700 capitalize min-w-[9rem] text-center">
            {MAANDEN[maand - 1]} {jaar}
          </span>
          <button
            onClick={volgendeMaand}
            aria-label="Volgende maand"
            className="p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-600"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 1. Niet ingelogd */}
      {!isAuthenticated && (
        <Melding emoji="🔒">
          <p>Log in met je Microsoft-account om het teamrooster te zien.</p>
          <p className="text-xs mt-2 text-slate-400">
            Gebruik de knop <span className="font-medium">Inloggen met Microsoft</span> rechtsboven.
          </p>
        </Melding>
      )}

      {/* 2. Laden */}
      {isAuthenticated && laden && (
        <Melding emoji="⏳"><p>Teamrooster laden…</p></Melding>
      )}

      {/* 3. Geen toegang (403) of andere serverfout */}
      {isAuthenticated && !laden && fout && (
        <Melding emoji="🔒">
          <p>{fout}</p>
        </Melding>
      )}

      {/* 4. Rooster opgehaald — per week een dag-weergave (dienst-rijen × dag-kolommen) */}
      {isAuthenticated && !laden && !fout && data && (
        data.gepubliceerd === false ? (
          <Melding emoji="🔒"><p>Het teamrooster voor {maandLabel} is nog niet gepubliceerd door de teamleider.</p></Melding>
        ) : !Array.isArray(data.weken) || data.weken.length === 0 ? (
          <Melding emoji="📅"><p>Voor {maandLabel} is er geen teamrooster.</p></Melding>
        ) : (
          <div className="space-y-4">
            {data.weken.map((week) => {
              const kantoorRij = bouwKantoorRij(week);
              // Kantoor achteraan: bouwDienstRijen sorteert op gemeente, deze rij hoort daar
              // niet in mee te lopen.
              const rijen = kantoorRij ? [...bouwDienstRijen(week), kantoorRij] : bouwDienstRijen(week);
              const maandag = weekNaarMaandag(week.weeknummer, week.isoJaar ?? jaar);
              const bijz = {};
              for (const d of (week.dagen || [])) { if (d.bijzonderheid) bijz[d.dag] = d.bijzonderheid; }
              const heeftBijz = Object.keys(bijz).length > 0;
              // Match op BEIDE velden: rond de jaarwisseling bestaat week 1 in twee ISO-jaren.
              const isHuidigeWeek = week.weeknummer === huidigeWeek && week.isoJaar === huidigIsoJaar;
              return (
                <div
                  key={`${week.isoJaar}-${week.weeknummer}`}
                  // De ref hangt bewust op deze container, BOVEN de twee CSS-paden hieronder
                  // (hidden lg:block en lg:hidden), zodat desktop en mobiel één anker delen.
                  ref={isHuidigeWeek ? huidigeWeekRef : null}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
                >
                  <div className="px-5 py-3 border-b border-brand-100 bg-brand-50 flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className="font-semibold text-brand-800">Week {week.weeknummer}</span>
                      {isHuidigeWeek && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-brand-100 text-brand-700">deze week</span>
                      )}
                    </span>
                  </div>

                  {rijen.length === 0 ? (
                    <div className="px-5 py-4 text-sm text-slate-400">Geen diensten deze week.</div>
                  ) : (
                    <>
                    <div className="overflow-x-auto hidden lg:block">
                      <table className="w-full text-xs border-collapse" style={{ minWidth: 700 }}>
                        <thead>
                          <tr className="border-b border-slate-100">
                            <th className="text-left py-3 pl-5 pr-3 w-44 text-slate-500 font-medium">Dienst</th>
                            {DAGEN.map((dag) => {
                              const weekend = dag === 'za' || dag === 'zo';
                              const datum = new Date(maandag);
                              datum.setDate(maandag.getDate() + DAGEN.indexOf(dag));
                              const datumStr = datum.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
                              return (
                                <th
                                  key={dag}
                                  className={`text-center py-3 px-2 font-medium ${weekend ? 'text-violet-700 bg-violet-50/40' : 'text-slate-700'}`}
                                  style={{ minWidth: 90 }}
                                >
                                  <div>{DAG_KORT[dag]}</div>
                                  <div className="font-normal text-slate-400" style={{ fontSize: 10 }}>{datumStr}</div>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {rijen.map((rij, rijIdx) => (
                            <tr key={rij.key} className={`border-t border-slate-100 align-top ${rijIdx % 2 === 0 ? 'bg-slate-50/50' : ''}`}>
                              <td className="py-2.5 pl-5 pr-3 whitespace-nowrap">
                                <span className="text-brand-700 font-semibold">{rij.gemeente}</span>
                                {rij.dienst && <span className="text-slate-400"> · {rij.dienst}</span>}
                              </td>
                              {DAGEN.map((dag) => {
                                const namen = rij.perDag[dag] || [];
                                const weekend = dag === 'za' || dag === 'zo';
                                const afw = rij.afwezigPerDag?.[dag] || {};
                                return (
                                  <td key={dag} className={`py-2.5 px-2 text-center text-slate-700 leading-relaxed ${weekend ? 'bg-violet-50/40' : ''}`}>
                                    {namen.length > 0
                                      ? namen.map((n, i) => {
                                          const t = afw[n];
                                          if (t) {
                                            // Kleur uit de gedeelde helper; text-decoration-color volgt currentColor,
                                            // dus de streep kleurt vanzelf mee met het type.
                                            const { label, kleur } = afwezigStijl(t);
                                            return (
                                              <span key={i} className="block line-through" style={{ color: kleur }}>
                                                {n}<span className="no-underline"> · {label}</span>
                                              </span>
                                            );
                                          }
                                          return <span key={i} className="block">{n}</span>;
                                        })
                                      : <span className="text-slate-300">—</span>}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                        {heeftBijz && (
                          <tfoot>
                            {/* Gele achtergrond op de hele rij: de notities zijn geen roosterinhoud
                                maar een aparte categorie. Alleen hier — de matrix in Weekrooster
                                heeft op deze plek invoervelden, en geel leest daar als 'gewijzigd'. */}
                            <tr style={{ background: '#FEF9C3' }}>
                              <td className="text-left py-2.5 pl-5 pr-3 w-44 text-slate-600 font-bold align-top border-t border-slate-200">
                                Bijzonderheden
                              </td>
                              {DAGEN.map((dag) => (
                                <td
                                  key={dag}
                                  className="py-2.5 px-2 text-center text-xs text-slate-700 align-top whitespace-pre-line leading-relaxed border-t border-slate-200"
                                >
                                  {bijz[dag]}
                                </td>
                              ))}
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                    {/* Daglijst onder lg: zelfde data (bouwDienstRijen + maandag) als kaarten per dag */}
                    <div className="lg:hidden divide-y divide-slate-100">
                      {DAGEN.map((dag) => {
                        const dagRijen = rijen.filter((rij) => (rij.perDag[dag] || []).length > 0);
                        if (dagRijen.length === 0) return null; // dag zonder dienst overslaan
                        const weekend = dag === 'za' || dag === 'zo';
                        const datum = new Date(maandag);
                        datum.setDate(maandag.getDate() + DAGEN.indexOf(dag));
                        const datumStr = datum.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
                        return (
                          <div key={dag} className={`px-5 py-3 ${weekend ? 'bg-violet-50/40' : ''}`}>
                            <div className={`text-sm font-semibold ${weekend ? 'text-violet-700' : 'text-slate-700'}`}>
                              {DAG_LANG[dag]} {datumStr}
                            </div>
                            <div className="mt-2 space-y-2">
                              {dagRijen.map((rij) => {
                                const namen = rij.perDag[dag] || [];
                                const afw = rij.afwezigPerDag?.[dag] || {};
                                return (
                                  <div key={rij.key}>
                                    <div className="text-xs font-medium text-slate-600">
                                      {rij.gemeente}
                                      {rij.dienst && <span> · {rij.dienst}</span>}
                                    </div>
                                    <div className="mt-1 flex flex-wrap gap-1.5">
                                      {namen.map((n, i) => {
                                        const t = afw[n];
                                        if (t) {
                                          const { label, kleur } = afwezigStijl(t);
                                          return (
                                            <span key={i} className="px-2 py-0.5 rounded text-xs font-medium bg-slate-50 line-through" style={{ color: kleur }}>
                                              {n}<span className="no-underline"> · {label}</span>
                                            </span>
                                          );
                                        }
                                        return <span key={i} className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">{n}</span>;
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                      {heeftBijz && (
                        <div className="px-5 py-3" style={{ background: '#FEF9C3' }}>
                          <div className="text-sm font-bold text-slate-600">Bijzonderheden</div>
                          <div className="mt-1 space-y-1">
                            {DAGEN.filter((dag) => bijz[dag]).map((dag) => {
                              const weekend = dag === 'za' || dag === 'zo';
                              const datum = new Date(maandag);
                              datum.setDate(maandag.getDate() + DAGEN.indexOf(dag));
                              const datumStr = datum.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
                              return (
                                <div key={dag} className="text-xs text-slate-600 whitespace-pre-line leading-relaxed">
                                  <span className={`font-medium ${weekend ? 'text-violet-700' : 'text-slate-700'}`}>{DAG_LANG[dag]} {datumStr}:</span> {bijz[dag]}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
